'use client';
/**
 * Q4 공유 FAB (2026-11-05 · Node 「정보방 버튼 바로 위에 공유 버튼」) — 오른쪽 가장자리 「정보방」 태그(OpenchatTag) 바로 위에 같은 모양으로 쌓는 「공유」 태그.
 *
 *  · 노출 조건 = 정보방 태그와 «같다»: ClientShell 의 openchatOn && openchatTagAllowed(pathname) · 마운트 직후 표시.
 *    서버 HTML 에는 없다(정보방 태그와 동일 — 모든 접속자·검색엔진에 같은 HTML · 클로킹 없음 · fixed 라 CLS 0).
 *  · 자리·크기·그림자 = .kd-oc 규칙을 그대로 따라 34px(768+ 36px) + 8px 위(share-fab.css).
 *  · 동작: navigator.share 가 있으면 시스템 공유창(제목 = 문서 제목 · 주소 = canonical, utm_*·n_* 제거).
 *          없으면 작은 메뉴 — 주소 복사(「주소를 복사했습니다」) · 카카오톡(SDK·키 준비 시에만) · 네이버 밴드.
 *  · 카카오·복사 로직은 RX ShareDock 과 같은 lib/rx/share-client 를 쓴다(중복 구현 없음).
 *  · 계측: trackRx('share_fab', method, 'fab') — 내부 /api/events/cta 한 곳. 외부 전송·메일 없음.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Share2 } from 'lucide-react';
import { openchatTagAllowed } from '@/components/openchat/openchat-route';
import { bandShareUrl, pickShareUrl } from '@/lib/rx/share';
import { copyText, readyKakao, sendKakaoFeed, waitKakao } from '@/lib/rx/share-client';
import { trackRx } from '@/lib/rx/track';
import './share-fab.css';

export type ShareFabMethod = 'native' | 'menu' | 'copy' | 'kakao' | 'band';

/** 지금 페이지의 공유 대상(제목 · 정식 주소). 브라우저 전용. */
export function currentShareTarget(): { title: string; url: string } {
  const canon = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.getAttribute('href') ?? null;
  const url = pickShareUrl(canon, window.location.href);
  const title = (document.title || '').trim() || '카더라';
  return { title, url };
}

function hasNativeShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

const track = (m: ShareFabMethod) => trackRx('share_fab', m, 'fab');

export default function ShareFab() {
  const pathname = usePathname() ?? '';
  const allowed = openchatTagAllowed(pathname);
  const [shown, setShown] = useState(false);
  const [tucked, setTucked] = useState(false);
  const [open, setOpen] = useState(false);
  const [kakao, setKakao] = useState(false);
  const [note, setNote] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!allowed) return;
    setShown(true);
    // 입력칸에 글자를 치는 동안만 비킨다(정보방 태그와 같은 규칙) — 화면 키보드가 리드폼 제출 버튼을 가리지 않게.
    const onFocus = () => {
      const a = document.activeElement as HTMLElement | null;
      setTucked(!!a && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName) && !root.current?.contains(a));
    };
    const onBlur = () => window.setTimeout(onFocus, 0);
    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', onBlur);
    return () => {
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('focusout', onBlur);
    };
  }, [allowed, pathname]);

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => (shown ? waitKakao(() => setKakao(true)) : undefined), [shown]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // 메뉴 바깥 누름 · Esc = 닫기
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); btn.current?.focus(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    document.addEventListener('keydown', onKey);
    root.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const flash = useCallback((msg: string) => {
    setNote(msg);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setNote(''), 1800);
  }, []);

  const onMain = async () => {
    if (hasNativeShare()) {
      track('native');
      const t = currentShareTarget();
      try { await navigator.share({ title: t.title, url: t.url }); } catch { /* 사용자가 닫음 */ }
      return;
    }
    if (!open) track('menu');
    setOpen((v) => !v);
  };

  const onCopy = async () => {
    track('copy');
    const ok = await copyText(currentShareTarget().url);
    setOpen(false);
    flash(ok ? '주소를 복사했습니다' : '주소를 복사하지 못했습니다');
    btn.current?.focus();
  };

  const onKakao = () => {
    track('kakao');
    const t = currentShareTarget();
    if (readyKakao()) sendKakaoFeed({ url: t.url, linkUrl: t.url, title: t.title });
    setOpen(false);
  };

  const onBand = () => {
    track('band');
    const t = currentShareTarget();
    try { window.open(bandShareUrl(t.url, t.title), '_blank', 'noopener,noreferrer'); } catch { /* 팝업 차단 */ }
    setOpen(false);
  };

  if (!allowed || !shown) return null;
  return (
    <div ref={root} className={`kd-shf${tucked ? ' is-tucked' : ''}`} data-share-fab="">
      {open ? (
        <div className="kd-shf__menu" role="menu" aria-label="공유 방법">
          <button type="button" role="menuitem" className="kd-shf__item" onClick={onCopy}>주소 복사</button>
          {kakao ? <button type="button" role="menuitem" className="kd-shf__item" onClick={onKakao}>카카오톡</button> : null}
          <button type="button" role="menuitem" className="kd-shf__item" onClick={onBand}>네이버 밴드</button>
        </div>
      ) : null}
      {note ? <div className="kd-shf__note" role="status">{note}</div> : null}
      <button
        ref={btn}
        type="button"
        className="kd-shf__btn"
        aria-label="공유하기"
        aria-haspopup={hasNativeShare() ? undefined : 'menu'}
        aria-expanded={hasNativeShare() ? undefined : open}
        data-cta="share_fab"
        onClick={onMain}
      >
        <Share2 className="kd-shf__ico" size={15} strokeWidth={2.4} aria-hidden="true" focusable="false" />
        <span className="kd-shf__t">공유</span>
      </button>
    </div>
  );
}
